import { useEffect, useState } from "react";
import { Button, Spinner } from "@fluentui/react-components";
import { runHandler } from "../../../integrations/tools";
import type { RefreshTokenFn } from "../../shared/api/fetchWithRefresh";
import { useDocReviewFindings } from "../hooks/useDocReviewFindings";
import { FindingCard } from "./FindingCard";
import { ChevronLeftIcon, ChevronRightIcon } from "../../shared/components/icons";
import type { Finding } from "../types";
import styles from "./DocReviewPanel.module.css";

interface DocReviewPanelProps {
  /** The conversation the `/check-references` skill ran in. Null before any
   *  scan has run this session — the panel then shows the empty state. */
  readonly threadId: string | null;
  readonly apiToken: string | null;
  readonly refreshTokens?: RefreshTokenFn;
}

/**
 * Standalone tab listing cross-reference/defined-terms findings (AIVA-7506)
 * for the current document-review thread — one durable card per finding,
 * each independently apply/skip-able, outliving the chat turn that found it.
 * Mirrors ApprovalsPanel's shape (separate from the inline chat flow).
 */
export function DocReviewPanel({ threadId, apiToken, refreshTokens }: Readonly<DocReviewPanelProps>) {
  const { findings, loading, error, actingId, clearFindings, applyFinding, skipFinding, jumpToFinding } =
    useDocReviewFindings(threadId, apiToken, refreshTokens);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [cleanupMessage, setCleanupMessage] = useState<string | null>(null);
  // Single-card carousel (AIVA-7506 follow-up): one finding visible at a
  // time, arrows to flip through, instead of a long stacked list. Clamped
  // rather than reset whenever `findings` changes length, so a decided row
  // further down the list never yanks the operator back to card 1 mid-review.
  const [activeIndex, setActiveIndex] = useState(0);
  const lastIndex = Math.max(0, findings.length - 1);
  const clampedIndex = Math.min(activeIndex, lastIndex);
  useEffect(() => {
    if (activeIndex > lastIndex) setActiveIndex(lastIndex);
  }, [activeIndex, lastIndex]);
  const current: Finding | undefined = findings[clampedIndex];

  const goPrev = () => setActiveIndex((i) => Math.max(0, i - 1));
  // findings.length is stable across an apply/skip (it changes a status, not
  // the row count), so reading it from this render's closure — rather than
  // waiting for the hook's post-action re-render — is safe here.
  const goNext = () => setActiveIndex((i) => Math.min(lastIndex, i + 1));

  // Apply/Skip settle the current card, then move on — the operator reviews
  // one finding, acts, and lands straight on the next one instead of having
  // to reach for the arrow themselves every single time.
  const handleApply = async (finding: Finding) => {
    await applyFinding(finding);
    goNext();
  };
  const handleSkip = async (finding: Finding) => {
    await skipFinding(finding);
    goNext();
  };

  const handleDoneReviewing = async () => {
    setCleaningUp(true);
    setCleanupMessage(null);
    try {
      const result = await runHandler("word", "word.docreview.cleanup_anchors", {}, 10000);
      const removed = (result.data as { removed?: number } | undefined)?.removed ?? 0;
      const markerWord = removed === 1 ? "marker" : "markers";
      setCleanupMessage(
        result.ok
          ? `Done — removed ${removed} hidden ${markerWord} from the document.`
          : `Could not clean up markers: ${result.error ?? "unknown error"}`,
      );
      // AIVA-8180: cleanup_anchors only removes the document's own hidden
      // content controls — it never touches the underlying Cosmos records,
      // so the findings this panel is still showing are now anchor-less.
      // Clear them locally rather than leaving cards the operator can no
      // longer act on (Apply/jump-to-location would just fail against a
      // missing anchor). Only on a successful cleanup: if it failed, the
      // anchors may still be there and the findings are still good.
      if (result.ok) clearFindings();
    } finally {
      setCleaningUp(false);
    }
  };

  let content;
  if (!threadId) {
    content = <p className={styles.empty}>Run /check-references in the chat to start a document review.</p>;
  } else if (loading && findings.length === 0) {
    content = <Spinner label="Loading findings…" />;
  } else if (findings.length === 0 || !current) {
    content = <p className={styles.empty}>No findings for this document yet.</p>;
  } else {
    content = (
      <div className={styles.carousel}>
        <button
          type="button"
          className={styles.navBtn}
          onClick={goPrev}
          disabled={clampedIndex === 0}
          aria-label="Previous finding"
        >
          <ChevronLeftIcon />
        </button>
        <div className={styles.carouselCard}>
          <FindingCard
            key={current.id}
            finding={current}
            busy={actingId === current.id}
            onApply={() => void handleApply(current)}
            onSkip={() => void handleSkip(current)}
            onJumpToLocation={() => void jumpToFinding(current)}
          />
        </div>
        <button
          type="button"
          className={styles.navBtn}
          onClick={goNext}
          disabled={clampedIndex === lastIndex}
          aria-label="Next finding"
        >
          <ChevronRightIcon />
        </button>
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <span className={styles.count}>
          {findings.length > 0 ? `Finding ${clampedIndex + 1} of ${findings.length}` : ""}
        </span>
        <div className={styles.headerActions}>
          <Button appearance="subtle" size="small" onClick={() => void handleDoneReviewing()} disabled={cleaningUp}>
            Done reviewing
          </Button>
        </div>
      </div>

      {error && (
        <div className={styles.error} role="alert">
          {error}
        </div>
      )}
      {cleanupMessage && <output className={styles.count}>{cleanupMessage}</output>}

      {content}
    </div>
  );
}
